const GAP_BELOW_TRIGGER = 8

/** Narrower than this and a filter list is more ellipsis than value, so the popover moves instead. */
const MIN_POPOVER_WIDTH = 240

/** Shorter than this and there is nothing left to read, so the popover climbs back over its trigger. */
const MIN_POPOVER_HEIGHT = 120

export type PopoverAnchor = { top: number; right: number; maxWidth: number; maxHeight: number }

type Viewport = { width: number; height: number }

type Bounds = { top: number; left: number; right: number; bottom: number }

/** Where a popover is allowed to reach: the table, minus however much of it has scrolled off screen. */
function toVisibleBounds(table: DOMRect, viewport: Viewport): Bounds {
  const top = Math.max(table.top, 0)
  const left = Math.max(table.left, 0)
  return {
    top,
    left,
    right: Math.max(Math.min(table.right, viewport.width), left),
    bottom: Math.max(Math.min(table.bottom, viewport.height), top),
  }
}

/**
 * Viewport coordinates for a popover hanging under its trigger and held inside the table. A popover
 * that reached past the table would be laid over the rest of the page, where a navigation bar or any
 * other chrome can cut it off.
 *
 * The two edges the popover is not pinned to are given as maxima rather than positions, so the browser
 * is the one that decides how much of what it wants it can have. That takes the popover's own size out
 * of the arithmetic — which is what lets the whole placement happen before it has ever been rendered.
 */
export function toAnchorInside(trigger: DOMRect, table: DOMRect, viewport: Viewport): PopoverAnchor {
  const bounds = toVisibleBounds(table, viewport)
  const below = Math.max(trigger.bottom + GAP_BELOW_TRIGGER, bounds.top)
  // The room under a trigger runs out when the table's header is near the foot of the screen.
  const top = Math.min(below, Math.max(bounds.bottom - MIN_POPOVER_HEIGHT, bounds.top))
  // Slides off its trigger only as far as the minimum demands, so it stays near what opened it.
  const rightEdge = Math.min(Math.max(trigger.right, bounds.left + MIN_POPOVER_WIDTH), bounds.right)
  return {
    top,
    right: viewport.width - rightEdge,
    maxWidth: Math.max(rightEdge - bounds.left, MIN_POPOVER_WIDTH),
    maxHeight: bounds.bottom - top,
  }
}

/** Built once per placement rather than per binding, so both popovers wear the anchor as one style. */
export function toAnchorStyle(anchor: PopoverAnchor): Record<string, string> {
  return {
    top: `${anchor.top}px`,
    right: `${anchor.right}px`,
    'max-width': `${anchor.maxWidth}px`,
    'max-height': `${anchor.maxHeight}px`,
  }
}
